import { useEffect, useMemo, useState } from 'react'
import type { AnalysisTimeRangeKey } from '../../../../types/analysis'
import { getWorkspacePulse, type WorkspacePulseEvent } from '../../../../services/aiAgentBridge'
import type { LiveModuleHeadline, LiveModuleRecord, LiveModuleSparkline } from '../LiveModuleCard'
import { bucketize, calculateDelta, timeRangeToDateRange } from './bucketize'

export interface UseRecommendationTrendsModuleParams {
  workspaceId: string
  pulseEvents: WorkspacePulseEvent[]
  timeRange: AnalysisTimeRangeKey
  refreshKey?: number
  now?: Date
}

export interface UseRecommendationTrendsModuleResult {
  headline: LiveModuleHeadline
  sparkline: LiveModuleSparkline
  records: LiveModuleRecord[]
  loading: boolean
  error: Error | null
}

const parseDate = (val: unknown): Date | null => {
  if (!val) return null
  if (val instanceof Date) return val
  if (typeof (val as { toDate?: () => Date }).toDate === 'function') {
    return (val as { toDate: () => Date }).toDate()
  }
  if (typeof val === 'string' || typeof val === 'number') {
    const d = new Date(val)
    return isNaN(d.getTime()) ? null : d
  }
  return null
}

const isRecommendationEvent = (event: WorkspacePulseEvent): boolean => {
  const type = String(event.type || '')
  if (type === 'AI_RESOLUTION_PROPOSAL' || /RECOMMEND|RESOLUTION/i.test(type)) return true
  const text = String(event.title || '') + ' ' + String(event.summary || '')
  return /recommendation|optimize|suggestion|resolve/i.test(text)
}

export const useRecommendationTrendsModule = ({
  workspaceId,
  pulseEvents,
  timeRange,
  refreshKey,
  now = new Date(),
}: UseRecommendationTrendsModuleParams): UseRecommendationTrendsModuleResult => {
  const [pulseRisks, setPulseRisks] = useState<Array<{ id: string; targetId: string; summary: string; source: string }>>([])
  const [pulseLoading, setPulseLoading] = useState<boolean>(true)

  useEffect(() => {
    if (!workspaceId) {
      setPulseRisks([])
      setPulseLoading(false)
      return
    }

    let isMounted = true
    setPulseLoading(true)

    getWorkspacePulse(workspaceId)
      .then((snapshot) => {
        if (isMounted) {
          setPulseRisks(snapshot.topRisks || [])
          setPulseLoading(false)
        }
      })
      .catch(() => {
        if (isMounted) {
          setPulseLoading(false)
        }
      })

    return () => {
      isMounted = false
    }
  }, [workspaceId, refreshKey])

  return useMemo(() => {
    const { from, to } = timeRangeToDateRange(timeRange, now)

    const recEvents = pulseEvents.filter((e) => {
      const d = parseDate(e.createdAt)
      return d && d >= from && d <= to && isRecommendationEvent(e)
    })

    const dates = recEvents
      .map((e) => parseDate(e.createdAt))
      .filter((d): d is Date => d !== null)

    const points = bucketize(dates, timeRange, now)
    const delta = calculateDelta(points)

    // Merge recommendation events + pulse top risks
    const eventRecords: LiveModuleRecord[] = recEvents.map((e, index) => ({
      id: e.id || `rec-event-${index}`,
      primary: e.title?.trim() || e.summary?.trim() || 'AI Recommendation',
      secondary: `${e.severity || 'INFO'} · ${e.targetType || 'WORKSPACE'}`,
      drillTarget: 'SUMMARY',
    }))

    const riskRecords: LiveModuleRecord[] = pulseRisks.map((r, index) => ({
      id: r.id || `risk-rec-${index}`,
      primary: r.summary || 'Proactive optimization alert',
      secondary: `${r.source.replace(/_/g, ' ')} · Target ${r.targetId}`,
      drillTarget: 'SUMMARY',
    }))

    const allRecords = [...eventRecords, ...riskRecords]
    const totalCount = allRecords.length

    const headline: LiveModuleHeadline = totalCount === 0
      ? { value: '—', label: 'no records' }
      : { value: totalCount, delta, label: 'recommendations' }

    return {
      headline,
      sparkline: { points, direction: 'up-is-good' },
      records: allRecords.slice(0, 5),
      loading: pulseLoading,
      error: null,
    }
  }, [pulseEvents, pulseRisks, timeRange, now, pulseLoading])
}
